import { Box, Input, Select, Textarea, Button, Icon, Flex, Text, Checkbox, useColorModeValue} from '@chakra-ui/react';
import React, { useState, useCallback, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import Formfield from 'views/global/field';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { AsyncSelect } from "chakra-react-select";
import { userController } from 'lib/user';
import SimpleUpload from "views/global/SimpleUpload";
import BeatLoader from "react-spinners/BeatLoader";

// Field types that always take the full row width in grid mode (lists, long
// text, headings) — everything else pairs up two-per-row.
const GRID_FULL_WIDTH_TYPES = ["heading", "textarea", "multiple", "editor", "file", "divider"];
// Inputs, textareas, and dropdowns all keep their original look (native
// border, label-above via the plain Formfield) — only the 2-per-row grid
// placement is new; nothing gets the merged single-border "boxed" style.
const GRID_UNBOXED_TYPES = [
    "text", "email", "password", "date", "hidden", "datetime-local", "number", "tel", "time", "url",
    "textarea", "select", "dynamic-select", "checkbox",
];

const FormGenerate = ({ fields, setFields, defaultValue, grid }) => {
    const {dynamicRecord, attachmentUpload, deleteRecord, userDetail} = userController();
    const { getUser } = userDetail();
    const role = getUser.role;
    const userID = getUser.id;
    const muteColor = useColorModeValue("gray.400", "white");
    const [attachments, setAttachments] = useState(defaultValue.attachments ? defaultValue.attachments : []);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(Array(attachments.length).fill(false));
    
    
    const advanceGroup  = ["admin","office"];
    
    const [sections, setSections] = useState(() => {
        return fields
            .filter(field => field.type === "multiple")
            .map(field => ({
                id: field.name,
                fields: defaultValue && defaultValue[field.name] ? defaultValue[field.name] : []
            }));
    });

    // `sections` only seeds from `fields` on mount. A "multiple" field that's
    // conditionally hidden at mount (e.g. Owners, hidden until "Enable Contract"
    // is checked) has no section yet when it later appears — add it here rather
    // than crashing at the `sections.find(...)` below. Only adds missing
    // sections, never touches ones already there, so in-progress edits survive.
    useEffect(() => {
        setSections(prev => {
            const existingIds = new Set(prev.map(section => section.id));
            const missing = fields
                .filter(field => field.type === "multiple" && !existingIds.has(field.name))
                .map(field => ({
                    id: field.name,
                    fields: defaultValue && defaultValue[field.name] ? defaultValue[field.name] : []
                }));
            return missing.length ? [...prev, ...missing] : prev;
        });
    }, [fields, defaultValue]);

    const addField = useCallback((sectionId, additionalFields) => {
        setSections(prev => {
            const newSections = prev.map(section => {
                if (section.id === sectionId) {
                    const newFields = additionalFields.map(field => ({ [field]: "" }));
                    return { ...section, fields: [...section.fields, { ...Object.assign({}, ...newFields), id: `${sectionId}-${Math.random().toString(36).substring(7)}` }] };
                }
                return section;
            });
            return newSections;
        });
    }, []);

    const removeField = useCallback((sectionId, fieldIndex) => {
        setSections(prev => {
            const newSections = prev.map(section => {
                if (section.id === sectionId) {
                    return { ...section, fields: section.fields.filter((_, index) => index !== fieldIndex) };
                }
                return section;
            });
            return newSections;
        });

        setFields(prev => {
            if (!prev[sectionId]) {
                return prev;
            }
            const newFields = prev[sectionId].filter((_, index) => index !== fieldIndex);
            return { ...prev, [sectionId]: newFields };
        });
    }, [setFields]);

    const handleChange = useCallback((e, name) => {
        if (e === null) {
            // A cleared AsyncSelect (isClearable) fires onChange(null). Deleting the
            // key here (rather than nulling it) meant a cleared value never made it
            // into the save payload at all, so the backend's mass-update — which only
            // touches keys actually present in the request — silently kept the old
            // value (e.g. a removed task assignee "coming back" after save).
            setFields(prev => ({ ...prev, [name]: null }));
            return;
        }
        if (Array.isArray(e)) {
            //save as seprated by comma
            const value = e.map(item => item.value).join(",");
            setFields(prev => ({ ...prev, [name]: value }));
            return;
        }
        const { value } = e.target ? e.target : e;
        setFields(prev => ({ ...prev, [name]: value }));
    }, [setFields]);

    const handleChangeMultiple = useCallback((e, name, sectionId, index) => {
        const newSections = sections.map(section => {
            if (section.id === sectionId) {
                section.fields[index][name] = e.target ? e.target.value : e.value;
            }
            return section;
        });
        setSections(newSections);

        setFields(prev => ({ ...prev, [sectionId]: newSections.find(section => section.id === sectionId).fields }));
    }, [sections, setFields]);

    const inputFieldType = ["text", "email", "password","date","hidden","datetime-local","number","tel","time","url"];
    const dateTypeFields = ["date","datetime-local"];

    const fieldValue = (sectionId, indexId, item) => {
        const row = sections.find((section) => section.id === sectionId)?.fields[indexId];
        if(row){
            if(row[item]){
                return row[item];
            }else{
                return false;
            }
        }else{
            return false;
        }
    };

    const formatDate = (InputDate, type) => {
        // Convert the Laravel date string to a JavaScript Date object
        const date = new Date(InputDate);
        // Format the date components
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is zero-based
        const day = date.getDate().toString().padStart(2, '0');
        if(type === "date"){
            return `${year}-${month}-${day}`
        }else if(type === "datetime-local"){
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        }
    };

    const checkArrayData = (name, item) => {
        if(name.includes("__")){
            const splitName = name.split("__");
            if(splitName[0] in item && splitName[1] in item[splitName[0]]){
                return item[splitName[0]][splitName[1]];
            }
        }
    }
    return (
        <Box
            display={grid ? "grid" : "block"}
            gridTemplateColumns={grid ? { base: "1fr", md: "1fr 1fr" } : undefined}
            columnGap={grid ? "20px" : undefined}
        >
            {fields.map(field => (

                field.type === "heading" ? (
                    <Box
                        key={field.name}
                        gridColumn={grid ? "1 / -1" : undefined}
                        mt={field.first ? 0 : "10px"}
                        mb="18px"
                    >
                        <Text fontSize="13px" fontWeight="700" color="brand.600" textTransform="uppercase" letterSpacing="0.04em" mb="8px">
                            {field.label}
                        </Text>
                        <hr className="form_divider" />
                    </Box>
                ) :

                (advanceGroup.includes(role) || field.permissions?.includes(role))  &&
                (
                    !field.permissionType || // If permissionType is not set, ignore the rest of the condition
                    (
                        !advanceGroup.includes(role) ? (
                            // If advanceGroup does not include the role, check the permissionType
                            !field.permissionType || // If permissionType is not set, ignore the rest
                            (
                                field.permissionType === "new" && // Check if permissionType is "new"
                                (!defaultValue["user_id"] || defaultValue["user_id"] == userID) // Ensure user_id condition
                            )
                        ) : true // If advanceGroup includes the role, just return true
                    )
                ) && ( 
                    <Formfield
                        label={field.type === "multiple" && field.itemLabel ? undefined : field.label}
                        help={field.type === "checkbox" ? undefined : field.help}
                        required={field.required && !(field.type === "password" && defaultValue["id"]) ? field.required : false}
                        display="block"
                        boxed={grid && !GRID_UNBOXED_TYPES.includes(field.type) && field.boxed !== false}
                        gridColumn={grid ? (GRID_FULL_WIDTH_TYPES.includes(field.type) || field.wide ? "1 / -1" : undefined) : undefined}
                        key={field.name}
                    >
                        {inputFieldType.includes(field.type) && (
                            <Input
                                type={field.type}
                                placeholder={field.placeholder ? field.placeholder : `Inserisci ${field.label}`}
                                name={field.name}
                                defaultValue={dateTypeFields.includes(field.type) && defaultValue[field.name] ? formatDate(defaultValue[field.name], field.type) : (field.split ? checkArrayData(field.name, defaultValue) : defaultValue[field.name])}
                                onChange={(e) => handleChange(e, field.name)}
                                key={field.name+"_"+defaultValue["id"]}
                                isRequired={field.required}
                                isDisabled={field.disabled}
                            />
                        )}
                        {field.type === "divider" && (
                            <hr className='form_divider' />
                        )}
                        {field.type === "checkbox" && (
                            <Flex align="center" gap="10px">
                                <Checkbox
                                    id={field.name}
                                    name={field.name}
                                    isChecked={!!defaultValue[field.name]}
                                    onChange={(e) => handleChange({ target: { value: e.target.checked } }, field.name)}
                                    key={field.name+"_"+defaultValue["id"]}
                                />
                                {field.help && (
                                    <Text fontSize="xs" color={muteColor}>{field.help}</Text>
                                )}
                            </Flex>
                        )}
                        {field.type === "file" && (
                            <>
                            <SimpleUpload
                                w="100%"
                                minH="200px"
                                mb={attachments.length > 0 ? 5 : 0}
                                params={{accept: '.jpg,.png,.gif,.jpeg', multiple: true, maxSize: 5000000}}
                                key={field.name+"_"+defaultValue["id"]}
                                content={
                                    <>
                                    {loading ?
                                    <BeatLoader
                                    loading={true}
                                    size={15}
                                    aria-label="Loading Spinner"
                                    color="#422CFA"
                                    /> :
                                    <Box>
                                        <Icon as={Upload} w='42px' h='42px' color="brand.600" />
                                        <Flex justify='center' mx='auto' mb='12px'>
                                            <Text fontSize='lg' fontWeight='700' color="brand.600">
                                                Carica File
                                            </Text>
                                        </Flex>
                                        <Text fontSize='sm' fontWeight='500' color='secondaryGray.500'>
                                            Sono consentite solo immagini
                                        </Text>
                                    </Box> }
                                    </>
                                }
                                onUpload={(accepted, rejected)=>{
                                    if(rejected.length > 0){
                                        const errors = {};
                                        rejected.map((item, key) => {
                                            errors[key] = item.errors[0].message;
                                        });
                                        return;
                                    }
                                    if(accepted.length > 0){
                                        setLoading(true);
                                        let uploadCount = 0;
                                        const formData = new FormData();
                                        accepted.map((item, key) => {
                                            const uploadID      = defaultValue["id"] ? defaultValue["id"] : field.uploadID + process.env.REACT_APP_RANDOM_ID;
                                            setFields(prev => ({ ...prev, "attachment_id": uploadID}));
                                            formData.append(field.uploadKey, uploadID);
                                            formData.append('file', accepted[key]);
                                            attachmentUpload(field.uploadURL,formData).then((response) => {
                                                uploadCount++;
                                                setAttachments(prev => [...prev, response.data]);
                                                if (uploadCount === accepted.length) {
                                                    setLoading(false);
                                                }
                                            })
                                            formData.delete(field.uploadKey);
                                            formData.delete('file');
                                        });
                                    }
                                }}
                            />
                            <Box
                            display={attachments.length > 0 ? "block" : "none"}
                            >
                                {attachments.length > 0 && attachments.map((attachment, index) => (
                                    <Box
                                    key={index}
                                    className='attachment-preview'
                                    >
                                        <Box
                                        display="flex"
                                        justifyContent='center'
                                        alignItems='center'
                                        flexDirection='column'
                                        width='100%'
                                        height="100%"
                                        >
                                            {deleteLoading[index] ? (
                                                <BeatLoader
                                                loading={true}
                                                size={15}
                                                aria-label="Loading Spinner"
                                                color="#422CFA"
                                                />
                                            ) : (
                                            <>
                                            <Button
                                            variant="solid"
                                            colorScheme="red"
                                            size='xs'
                                            borderRadius="50%"
                                            p={0}
                                            my={1.5}
                                            ms={2}
                                            onClick={() => {
                                                const updatedLoading = [...deleteLoading]; // Copy the current loading state array
                                                updatedLoading[index] = true; // Set loading to true for the specific attachment being deleted
                                                setDeleteLoading(updatedLoading)
                                                deleteRecord(field.deleteURL,{id: attachment.id }).then(response => {
                                                    setAttachments(prev => prev.filter((_, i) => i !== index));
                                                }).finally(() => {
                                                    const updatedLoading = [...deleteLoading]; // Copy the current loading state array
                                                    updatedLoading[index] = false; // Set loading to false for the specific attachment being deleted
                                                    setDeleteLoading(updatedLoading); // Update the loading state array
                                                });
                                            }}
                                            >
                                                <X />
                                            </Button>
                                            <img src={process.env.REACT_APP_URL+"/"+attachment.path} alt={attachment.name} />
                                            </>
                                        )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            </>
                        )}
                        {field.type === "select" && (
                            <Select
                            id={field.name}
                            name={field.name}
                            required={field.required}
                            onChange={(e) => handleChange(e, field.name)}
                            key={field.name+"_"+defaultValue["id"]}
                            isRequired={field.required}
                            isDisabled={field.disabled}
                            >
                            <option value="">Seleziona {field.label}</option>
                            {field.options.map((option, optionIndex) => {
                                const optionValue = Array.isArray(option) ? option[1] : option;
                                const optionLabel = Array.isArray(option) ? option[0] : option;
                                return (
                                    <option key={optionIndex} value={optionValue} selected={defaultValue[field.name] === optionValue}>
                                        {optionLabel}
                                    </option>
                                );
                            })}
                            </Select>
                        )}
                        {field.type === "dynamic-select" && (() => {
                            const dependsOn    = field.depends_on;
                            const dependsValue = dependsOn ? defaultValue[dependsOn] : null;
                            const dependsReady = dependsOn ? !!dependsValue : true;
                            const resolvedSrc  = dependsOn
                                ? field.src + (field.src.includes('?') ? '&' : '?') + dependsOn + '=' + encodeURIComponent(dependsValue ?? '')
                                : field.src;
                            return (
                                <AsyncSelect
                                placeholder={dependsReady ? `cerca ${field.placeholder}` : `seleziona prima ${dependsOn.replace(/_id$/, '')}`}
                                loadOptions={(inputValue) => dependsReady ? dynamicRecord(resolvedSrc, inputValue, true) : Promise.resolve([])}
                                key={field.name+"_"+(dependsOn ? dependsValue : defaultValue["id"])}
                                required={field.required}
                                onChange={(e) => handleChange(e, field.name)}
                                isRequired={field.required}
                                isDisabled={!dependsReady}
                                name={field.name}
                                isClearable={true}
                                isMulti={field.multiple}
                                inputId={field.name}
                                defaultValue={()=>{
                                    if(!defaultValue[field.name]){
                                        return [];
                                    }
                                    if(!field.multiple){

                                        const baseKeyName   = field.name.replace(/_id$/, '');
                                        const basekeyLabel  = field.dynamic_label ? field.dynamic_label : "name";
                                        return {
                                            label: defaultValue[baseKeyName] && defaultValue[baseKeyName][basekeyLabel] ? defaultValue[baseKeyName][basekeyLabel] : defaultValue[baseKeyName],
                                            value: defaultValue[baseKeyName] && defaultValue[baseKeyName].id ? defaultValue[baseKeyName].id : defaultValue[baseKeyName]
                                        };
                                    }else{
                                        return defaultValue[field.name].map((item) => {
                                            return {
                                                label: item.name ? item.name : item,
                                                value: item.id ? item.id : item
                                            };
                                        });
                                    }
                                }}
                                defaultOptions={dependsReady}
                                cacheOptions={dependsReady ? resolvedSrc : false}
                                />
                            );
                        })()}
                        {field.type === "textarea" && (
                            <Textarea
                                placeholder={`Inserisci ${field.label}`}
                                name={field.name}
                                defaultValue={defaultValue[field.name]}
                                onChange={(e) => handleChange(e, field.name)}
                                key={field.name+"_"+defaultValue["id"]}
                                isRequired={field.required}
                                isDisabled={field.disabled}
                            />
                        )}

                        {field.type === "editor" && (
                            <CKEditor
                            editor={ClassicEditor}
                            data={defaultValue[field.name]}
                            onChange={(event, editor) => {
                                const data = editor.getData();
                                handleChange({ target: { value: data } }, field.name);
                            }}
                            key={field.name+"_"+defaultValue["id"]}
                            isRequired={field.required}
                            config={{
                                toolbar: [ 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote','|', 'undo', 'redo', '|', 'insertTable'],
                                heading: {
                                    options: [
                                        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                                        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                                        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                                        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
                                    ]
                                },
                                shouldNotGroupWhenFull: true,
                                image: {
                                    toolbar: []
                                }
                            }}
                        />
                        )}

                        {field.type === "multiple" && (() => {
                            const rows = sections.find((section) => section.id === field.name)?.fields ?? [];
                            const addLabel = field.itemLabel ? `+ Aggiungi ${field.itemLabel}` : "+ Aggiungi Nuovo";

                            const renderInput = (addField, sectionFieldIndex) => {
                                const defaultValueItem = fieldValue(field.name, sectionFieldIndex, addField.name) ? fieldValue(field.name, sectionFieldIndex, addField.name) : "";
                                const defaultIDItem     = fieldValue(field.name, sectionFieldIndex, "id") ? fieldValue(field.name, sectionFieldIndex, "id") : addField.name+"-"+sectionFieldIndex;
                                if (addField.type === "select") {
                                    return (
                                        <Select
                                            width="100%"
                                            id={addField.name}
                                            name={addField.name}
                                            isRequired={addField.required}
                                            onChange={(e) => handleChangeMultiple(e, addField.name, field.name, sectionFieldIndex)}
                                            key={defaultIDItem}
                                        >
                                            <option value="">Seleziona {addField.placeholder} </option>
                                            {addField.options.map((option, optionIndex) => {
                                                const optionValue = Array.isArray(option) ? option[1] : option;
                                                const optionLabel = Array.isArray(option) ? option[0] : option;
                                                return (
                                                    <option key={optionIndex} value={optionValue} selected={defaultValueItem === String(optionValue)}>
                                                        {optionLabel}
                                                    </option>
                                                );
                                            })}
                                        </Select>
                                    );
                                }
                                if (inputFieldType.includes(addField.type)) {
                                    return (
                                        <Input
                                            width="100%"
                                            type="text"
                                            name={addField.name}
                                            placeholder={`Inserisci ${addField.placeholder}`}
                                            isRequired={addField.required}
                                            onChange={(e) => handleChangeMultiple(e, addField.name, field.name, sectionFieldIndex)}
                                            key={defaultIDItem}
                                            value={defaultValueItem}
                                        />
                                    );
                                }
                                return null;
                            };

                            if (field.design === "horizontal") {
                                return (
                                    <>
                                    {rows.map((sectionField, sectionFieldIndex) => (
                                        <Box key={sectionField.id} display="flex" justifyContent="space-between" mb={5}>
                                            {field.multipleFields.map((addField) => (
                                                <Box key={addField.name} me={2} flexDirection="row" flex="1">
                                                    {renderInput(addField, sectionFieldIndex)}
                                                </Box>
                                            ))}
                                            <Button
                                                variant="outline"
                                                colorScheme="red"
                                                size='xs'
                                                borderRadius="50%"
                                                p={0}
                                                my={1.5}
                                                ms={2}
                                                onClick={() => removeField(field.name, sectionFieldIndex)}
                                            >
                                                <X />
                                            </Button>
                                        </Box>
                                    ))}
                                    <Button variant="link" colorScheme="brand" onClick={() => addField(field.name, field.multipleFields.map(f => f.name))}>
                                        {addLabel}
                                    </Button>
                                    </>
                                );
                            }

                            // Vertical layout (Owners, etc.): stacked fields with a tight,
                            // consistent gap, a numbered heading, the remove action pinned
                            // under the fields, and a plain divider — not a bordered/padded
                            // sub-box — between entries.
                            return (
                                <>
                                {rows.map((sectionField, sectionFieldIndex) => (
                                    <Box key={sectionField.id}>
                                        {sectionFieldIndex > 0 && (
                                            <hr className="form_divider" style={{ margin: "18px 0" }} />
                                        )}
                                        {field.itemLabel && (
                                            <Text fontSize="12px" fontWeight="700" color="brand.600" textTransform="uppercase" letterSpacing="0.03em" mb="10px">
                                                {field.itemLabel} {sectionFieldIndex + 1}
                                            </Text>
                                        )}
                                        <Flex direction="column" gap="10px">
                                            {field.multipleFields.map((addField) => (
                                                <Box key={addField.name}>
                                                    {renderInput(addField, sectionFieldIndex)}
                                                </Box>
                                            ))}
                                        </Flex>
                                        <Flex justifyContent="flex-end" mt="10px">
                                            {!(field.keepFirst && sectionFieldIndex === 0) && (
                                            <Button
                                                variant="ghost"
                                                colorScheme="red"
                                                size="xs"
                                                leftIcon={<X size={14} />}
                                                onClick={() => removeField(field.name, sectionFieldIndex)}
                                            >
                                                Rimuovi {field.itemLabel || ""}
                                            </Button>
                                            )}
                                        </Flex>
                                    </Box>
                                ))}
                                <Button
                                    variant="outline"
                                    colorScheme="brand"
                                    size="sm"
                                    mt={rows.length > 0 ? "14px" : 0}
                                    onClick={() => addField(field.name, field.multipleFields.map(f => f.name))}
                                >
                                    {addLabel}
                                </Button>
                                </>
                            );
                        })()}
                    </Formfield>
                )
            ))}
        </Box>
    );
};
export default React.memo(FormGenerate);
